import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Drawer, FloatButton, Input, Space, Switch, Tag, Typography, message } from 'antd';
import { BugOutlined, ArrowRightOutlined, LogoutOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../../../api';
import { routes } from '../../../config/routes';
import { useAppStore } from '../../../store';
import {
  HOME_ACCOUNT_GUIDE_RESET_EVENT,
  clearHomeAccountGuideRead,
} from '../../../utils/homeAccountGuide';
import './DevRouteLauncher.css';

const { Text } = Typography;

const DEV_BUSINESS_SHORTCUTS = [
  { path: '/marketing-operator', name: '数据总览' },
  { path: '/lead-hunter', name: '获客助手' },
  { path: '/community-manager', name: '社群运营官' },
  { path: '/content-scout', name: '素材情报员' },
  { path: '/product-specialist', name: '选品专员' },
  { path: '/marketing-operator/products', name: '我的货品' },
  { path: '/settings/accounts', name: '设置-我的账号' },
] as const;

const DEV_TOOL_SHORTCUTS = [
  { path: '/dev-tools', name: '开发调试中心' },
  { path: '/dev-tools/browser', name: '浏览器调试设置' },
  { path: '/dev-tools/douyin-im', name: '抖音私信调试' },
  { path: '/dev-tools/douyin-creator-publish', name: '创作者平台调试' },
  { path: '/product-selection/buying', name: '选品专员' },
  { path: '/dev-tools/app-update', name: '应用更新调试' },
  { path: '/login', name: '登录页' },
] as const;

const DEV_ROUTE_ALIASES: Record<string, string> = {
  '/marketing': '/marketing-operator',
  '/dashboard': '/marketing-operator',
  '/home': '/marketing-operator',
  '/marketing-operator/home': '/marketing-operator',
  '/products': '/marketing-operator/products',
  '/my-products': '/marketing-operator/products',
  '/device': '/settings/accounts',
  '/devices': '/settings/accounts',
  '/my-devices': '/settings/accounts',
  '/setting': '/settings/accounts',
  '/settings': '/settings/accounts',
  '/accounts': '/settings/accounts',
  '/lead': '/lead-hunter',
  '/leadhunter': '/lead-hunter',
  '/community': '/community-manager',
  '/communitymanager': '/community-manager',
  '/content': '/content-scout',
  '/contentscout': '/content-scout',
  '/product': '/product-specialist',
  '/specialist': '/product-specialist',
  '/debug': '/dev-tools',
  '/dev': '/dev-tools',
  '/devtools': '/dev-tools',
  '/dev-tools/im': '/dev-tools/douyin-im',
  '/dev-tools/creator': '/dev-tools/douyin-creator-publish',
  '/buying-product-selection': '/product-selection/buying',
  '/product-selection': '/product-selection/buying',
  '/dev-tools/update': '/dev-tools/app-update',
  '/dev-tools/browser-settings': '/dev-tools/browser',
};

interface ShortcutGroupConfig {
  title: string;
  description: string;
  shortcuts: readonly { path: string; name: string }[];
}

const DEV_SHORTCUT_GROUPS: ShortcutGroupConfig[] = [
  {
    title: '业务路由',
    description: '常用业务页直接跳转，保留原来的快速切页能力。',
    shortcuts: DEV_BUSINESS_SHORTCUTS,
  },
  {
    title: '调试工具',
    description: '复杂调试能力已经拆到独立页面，这里只保留导航入口。',
    shortcuts: DEV_TOOL_SHORTCUTS,
  },
];

function normalizePath(rawPath: string): string {
  const trimmed = rawPath.trim();
  if (!trimmed) {
    return '';
  }

  try {
    const parsed = new URL(trimmed);
    const hashPath = parsed.hash.replace(/^#/, '').trim();
    if (hashPath) {
      return normalizePath(hashPath);
    }
    return normalizePath(parsed.pathname);
  } catch {
    // 允许继续处理普通路径和 hash 路径输入
  }

  if (trimmed.startsWith('#/')) {
    return normalizePath(trimmed.slice(1));
  }

  if (trimmed.startsWith('#')) {
    return normalizePath(trimmed.slice(1));
  }

  const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return normalized.replace(/\/+$/, '') || '/';
}

function createMockDevSession() {
  return {
    userId: `dev_debug_${Date.now()}`,
    user: {
      id: 'dev-debug-user',
      nickname: '开发调试账号',
    },
  };
}

export const DevRouteLauncher: React.FC = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [targetPath, setTargetPath] = useState(location.pathname);
  const authUserId = useAppStore((state) => state.auth.userId);
  const isAuthenticated = Boolean(authUserId);
  const setAuthSession = useAppStore((state) => state.setAuthSession);
  const clearAuthSession = useAppStore((state) => state.clearAuthSession);
  const devLauncherHidden = useAppStore((state) => state.displayWorkbench.devLauncherHidden);
  const setDevLauncherHidden = useAppStore((state) => state.setDevLauncherHidden);
  const storyboardEnabled = useAppStore((state) => state.displayWorkbench.storyboardEnabled);
  const setStoryboardEnabled = useAppStore((state) => state.setStoryboardEnabled);

  // Ctrl+Shift+D 切换调试按钮显隐
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.ctrlKey && e.shiftKey && e.key === 'D') {
      e.preventDefault();
      setDevLauncherHidden(!devLauncherHidden);
    }
  }, [devLauncherHidden, setDevLauncherHidden]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    setTargetPath(location.pathname);
  }, [location.pathname]);

  const routePathSet = useMemo(() => new Set(routes.map((route) => route.path)), []);

  const shortcutGroups = useMemo(() => {
    return DEV_SHORTCUT_GROUPS.map((group) => ({
      ...group,
      shortcuts: group.shortcuts.map((shortcut) => {
        const matchedRoute = routes.find((route) => route.path === shortcut.path);
        return {
          path: shortcut.path,
          name: matchedRoute?.name || shortcut.name,
        };
      }),
    }));
  }, []);

  const resolveTargetPath = (rawPath: string) => {
    const normalizedPath = normalizePath(rawPath);
    if (!normalizedPath) {
      return null;
    }

    const aliasedPath = DEV_ROUTE_ALIASES[normalizedPath] || normalizedPath;
    return routePathSet.has(aliasedPath) ? aliasedPath : null;
  };

  const gotoPath = (rawPath: string) => {
    const nextPath = resolveTargetPath(rawPath);
    if (!nextPath) {
      messageApi.warning('未识别该调试路由，请从快捷入口选择或输入有效路径');
      return;
    }

    if (nextPath === '/login') {
      clearAuthSession();
      navigate('/login');
      setOpen(false);
      return;
    }

    if (!isAuthenticated) {
      setAuthSession(createMockDevSession());
    }

    navigate(nextPath);
    setOpen(false);
  };

  const renderFloatButton = () => (
    <FloatButton
      icon={<BugOutlined />}
      type="primary"
      tooltip="开发调试入口"
      onClick={() => setOpen(true)}
    />
  );

  const renderCurrentRoute = () => (
    <section className="dev-route-launcher__section">
      <Text type="secondary">当前路由</Text>
      <div className="dev-route-current">{location.pathname}</div>
    </section>
  );

  const renderTargetInput = () => (
    <section className="dev-route-launcher__section">
      <Text type="secondary">输入目标路由</Text>
      <Space.Compact className="dev-route-input-row">
        <Input
          value={targetPath}
          placeholder="/dev-tools 或 #/lead-hunter"
          onChange={(event) => setTargetPath(event.target.value)}
          onPressEnter={() => gotoPath(targetPath)}
        />
        <Button type="primary" icon={<ArrowRightOutlined />} onClick={() => gotoPath(targetPath)}>
          进入
        </Button>
      </Space.Compact>
    </section>
  );

  const renderShortcutGroup = (group: (typeof shortcutGroups)[number]) => (
    <section key={group.title} className="dev-route-launcher__section">
      <div className="dev-route-launcher__section-header">
        <Text type="secondary">{group.title}</Text>
        <Text type="secondary" className="dev-route-hint">
          {group.description}
        </Text>
      </div>
      <div className="dev-route-tags">
        {group.shortcuts.map((route) => (
          <Tag
            key={route.path}
            className="dev-route-tag"
            color={route.path === location.pathname ? 'blue' : 'default'}
            onClick={() => gotoPath(route.path)}
          >
            {route.name}
          </Tag>
        ))}
      </div>
    </section>
  );

  const renderActionButtons = () => (
    <div className="dev-route-actions">
      <Button block type="primary" onClick={() => gotoPath('/dev-tools')}>
        打开开发调试中心
      </Button>
      <Button block onClick={() => gotoPath('/marketing-operator')}>
        进入数据总览
      </Button>
      <Button
        block
        onClick={() => {
          clearHomeAccountGuideRead(authUserId);
          window.dispatchEvent(new Event(HOME_ACCOUNT_GUIDE_RESET_EVENT));
          messageApi.success('已清除首页选择提示已读标识');
        }}
        disabled={!authUserId}
      >
        清除首页选择提示已读标识
      </Button>
      <Button
        block
        icon={<LogoutOutlined />}
        onClick={async () => {
          try {
            await logout();
          } catch {
            // 即使接口失败也继续清理本地状态
          }
          clearAuthSession();
          setTargetPath('/login');
          navigate('/login');
          setOpen(false);
        }}
      >
        登出并回到登录页
      </Button>
    </div>
  );

  const renderFeatureSwitches = () => (
    <section className="dev-route-launcher__section">
      <div className="dev-route-launcher__section-header">
        <Text type="secondary">功能开关</Text>
      </div>
      <div className="dev-route-launcher__section-header">
        <Text>故事版生成</Text>
        <Switch
          size="small"
          checked={storyboardEnabled}
          onChange={setStoryboardEnabled}
        />
      </div>
      <div className="dev-route-launcher__section-header">
        <Space>
          <EyeInvisibleOutlined />
          <Text>隐藏调试按钮</Text>
        </Space>
        <Switch
          size="small"
          checked={devLauncherHidden}
          onChange={(checked) => {
            setDevLauncherHidden(checked);
            if (checked) {
              setOpen(false);
              messageApi.info('调试按钮已隐藏，按 Ctrl+Shift+D 重新显示');
            }
          }}
        />
      </div>
    </section>
  );

  const renderHint = () => (
    <Text type="secondary" className="dev-route-hint">
      开发模式下进入业务路由时，会自动注入 mock 登录态。复杂调试功能已迁移到独立页面，避免继续堆在抽屉里和业务页里。
      {devLauncherHidden ? '' : '按 Ctrl+Shift+D 可隐藏/显示调试按钮。'}
    </Text>
  );

  const renderDrawerContent = () => (
    <div className="dev-route-launcher">
      {renderCurrentRoute()}
      {renderTargetInput()}
      {shortcutGroups.map(renderShortcutGroup)}
      {renderActionButtons()}
      {renderFeatureSwitches()}
      {renderHint()}
    </div>
  );

  const renderDrawer = () => (
    <Drawer
      title="开发调试入口"
      placement="right"
      size={360}
      onClose={() => setOpen(false)}
      open={open}
    >
      {renderDrawerContent()}
    </Drawer>
  );

  return (
    <>
      {contextHolder}
      {!devLauncherHidden && renderFloatButton()}
      {renderDrawer()}
    </>
  );
};

export default DevRouteLauncher;
